import { useMemo, useState } from 'react';
import { useDraggable } from '@dnd-kit/core';
import type { BlockNode, DocumentV3, TemplateDef } from '@docgen/schema';
import { BLOCK_DEFS, BLOCKS_BY_TYPE, type BlockCategory } from '@docgen/blocks';
import { BCE_TEMPLATES } from '@docgen/seed-bce';
import {
  BookmarkPlus,
  Copy,
  Database,
  FilePlus2,
  Files,
  GripVertical,
  LibraryBig,
  ListTree,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Search,
  SquarePlus,
  Trash2,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { usePersistedBoolean } from '../lib/persisted';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { addPage, duplicatePage, movePage, newPageId, removePage } from '../lib/doc-ops';
import {
  inDocumentOrder,
  insertIndexAfterSelection,
  pageFromTemplate,
  pageRange,
} from '../lib/library-pages';
import { PAGE_LIBRARY_MAX_PAGES, type EditorLibraryActions } from '../lib/page-library';
import { LibraryPicker } from '../library/LibraryPicker';
import { SaveToLibraryDialog } from '../library/SaveToLibraryDialog';
import { DataEditor } from './DataEditor';
import type { Selection } from './types';

/** What the page library needs from the editor (see EditorLibraryActions). */
export type PanelLibrary = EditorLibraryActions;

export interface PanelProps {
  doc: DocumentV3;
  selection: Selection;
  onSelect: (selection: Selection) => void;
  onChange: (next: DocumentV3, coalesceKey?: string) => void;
  onRevealBlock: (blockId: string) => void;
  onRevealPage: (pageId: string) => void;
  /** The page library (Save to library, From library); without it the Pages tab offers the built-in templates inline. */
  library?: PanelLibrary | undefined;
}

const CATEGORY_LABELS: Record<BlockCategory, string> = {
  layout: 'Layout',
  text: 'Text',
  media: 'Media',
  data: 'Data',
  brand: 'Brand',
};

const TABS = [
  { id: 'pages', label: 'Pages', icon: Files },
  { id: 'insert', label: 'Insert', icon: SquarePlus },
  { id: 'outline', label: 'Outline', icon: ListTree },
  { id: 'data', label: 'Data', icon: Database },
] as const;
type Tab = (typeof TABS)[number]['id'];

function PaletteCard({ type, name, description }: { type: string; name: string; description: string }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette:${type}`,
    data: { kind: 'new-block', blockType: type },
  });
  return (
    <button
      ref={setNodeRef}
      type="button"
      {...attributes}
      {...listeners}
      title={description}
      className={cn(
        'flex w-full cursor-grab items-center gap-2 rounded-md border bg-card px-2 py-1.5 text-left text-xs font-medium shadow-sm transition-colors hover:border-primary/60 hover:text-primary',
        isDragging && 'opacity-40',
      )}
    >
      <GripVertical className="size-3.5 shrink-0 text-muted-foreground" />
      {name}
    </button>
  );
}

function OutlineNode({ node, depth, selection, onSelect, onReveal }: {
  node: BlockNode;
  depth: number;
  selection: Selection;
  onSelect: (s: Selection) => void;
  onReveal: (blockId: string) => void;
}) {
  const def = BLOCKS_BY_TYPE.get(node.type);
  const selected = selection?.kind === 'block' && selection.id === node.id;
  const drag = useDraggable({
    id: `outline:${node.id}`,
    data: { kind: 'move-block', blockId: node.id },
  });
  return (
    <div>
      <div
        ref={drag.setNodeRef}
        className={cn(
          'group flex cursor-pointer items-center gap-1 rounded px-1.5 py-1 text-xs hover:bg-accent',
          selected && 'bg-primary/10 font-medium text-primary',
          drag.isDragging && 'opacity-40',
        )}
        style={{ paddingLeft: 6 + depth * 12 }}
        onClick={() => {
          onSelect({ kind: 'block', id: node.id });
          onReveal(node.id);
        }}
      >
        <span
          {...drag.attributes}
          {...drag.listeners}
          className="cursor-grab text-muted-foreground opacity-0 group-hover:opacity-100"
          aria-label="Drag to move"
        >
          <GripVertical className="size-3" />
        </span>
        <span className="truncate">{def?.name ?? node.type}</span>
        {node.repeat && (
          <span className="ml-auto font-mono text-[9px] text-muted-foreground">×{node.repeat.over}</span>
        )}
      </div>
      {(node.children ?? []).map((child) => (
        <OutlineNode
          key={child.id}
          node={child}
          depth={depth + 1}
          selection={selection}
          onSelect={onSelect}
          onReveal={onReveal}
        />
      ))}
    </div>
  );
}

/** The built-in BCE templates as a one-click list, for when the page library is off. */
function TemplateList({ onAdd }: { onAdd: (template: TemplateDef) => void }) {
  return (
    <>
      <p className="mb-1 mt-3 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        From template
      </p>
      <div className="space-y-1">
        {BCE_TEMPLATES.map((tpl) => (
          <button
            key={tpl.id}
            type="button"
            className="flex w-full items-center gap-1.5 rounded-md px-2 py-1 text-left text-xs text-muted-foreground hover:bg-accent hover:text-foreground"
            onClick={() => onAdd(tpl)}
          >
            <Plus className="size-3" />
            {tpl.name}
          </button>
        ))}
      </div>
    </>
  );
}

function PagesTab({ doc, selection, onSelect, onChange, onRevealPage, library }: PanelProps) {
  // Drag-to-reorder the page list (native DnD; `dragId` is the page being moved,
  // `overIndex` the row a drop would land before, for the insertion line).
  const [dragId, setDragId] = useState<string | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  // "Select pages" mode: the picked page ids, and the last one clicked (the shift-click anchor).
  const [picking, setPicking] = useState(false);
  const [picked, setPicked] = useState<readonly string[]>([]);
  const [anchorId, setAnchorId] = useState<string | null>(null);
  // The page ids the Save to library dialog is open for, and whether the picker is open.
  const [saving, setSaving] = useState<readonly string[] | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const endDrag = () => {
    setDragId(null);
    setOverIndex(null);
  };
  const stopPicking = () => {
    setPicking(false);
    setPicked([]);
    setAnchorId(null);
  };
  const addFromTemplate = (tpl: TemplateDef, atIndex?: number) => {
    const page = pageFromTemplate(tpl);
    onChange(addPage(doc, page, atIndex));
    onSelect({ kind: 'page', id: page.id });
    onRevealPage(page.id);
  };

  /** A click on a page row while picking: toggle it, or with shift pick the run from the last click. */
  const pickPage = (pageId: string, shift: boolean) => {
    if (shift && anchorId !== null) {
      const run = pageRange(doc.pages, anchorId, pageId);
      setPicked((current) => inDocumentOrder(doc.pages, [...new Set([...current, ...run])]));
    } else {
      setPicked((current) =>
        current.includes(pageId) ? current.filter((id) => id !== pageId) : inDocumentOrder(doc.pages, [...current, pageId]),
      );
    }
    setAnchorId(pageId);
  };

  // A picked page that was deleted meanwhile is no longer picked.
  const pickedNow = inDocumentOrder(doc.pages, picked);
  const tooMany = pickedNow.length > PAGE_LIBRARY_MAX_PAGES;
  const savingPages = saving ? doc.pages.filter((page) => saving.includes(page.id)) : [];

  return (
    <div className="space-y-1">
      {library ? (
        picking ? (
          <div className="mb-2 space-y-1.5 rounded-md border bg-accent/40 p-2" role="group" aria-label="Selected pages">
            <p className="text-xs font-medium">
              {pickedNow.length === 0 ? 'Click pages to select them' : `${pickedNow.length} selected`}
            </p>
            {tooMany ? (
              <p className="text-[11px] text-destructive">A library item holds up to {PAGE_LIBRARY_MAX_PAGES} pages.</p>
            ) : null}
            <div className="flex gap-1.5">
              <Button
                size="sm"
                className="flex-1"
                disabled={pickedNow.length === 0 || tooMany}
                onClick={() => setSaving(pickedNow)}
              >
                <BookmarkPlus /> Save to library
              </Button>
              <Button size="sm" variant="ghost" onClick={stopPicking}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="mb-1 flex justify-end">
            <button
              type="button"
              className="rounded px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground hover:bg-accent hover:text-foreground"
              onClick={() => setPicking(true)}
            >
              Select pages
            </button>
          </div>
        )
      ) : null}
      {doc.pages.map((page, i) => {
        const selected = selection?.kind === 'page' && selection.id === page.id;
        const isPicked = picking && pickedNow.includes(page.id);
        const pageName = page.name ?? page.id;
        return (
          <div
            key={page.id}
            draggable={!picking}
            onDragStart={(e) => {
              setDragId(page.id);
              e.dataTransfer.effectAllowed = 'move';
            }}
            onDragOver={(e) => {
              if (dragId === null) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
              setOverIndex(i);
            }}
            onDrop={(e) => {
              e.preventDefault();
              if (dragId !== null && dragId !== page.id) onChange(movePage(doc, dragId, i));
              endDrag();
            }}
            onDragEnd={endDrag}
            className={cn(
              'group flex cursor-pointer items-center gap-1.5 rounded-md px-2 py-1.5 text-xs hover:bg-accent',
              selected && !picking && 'bg-primary/10 text-primary',
              isPicked && 'bg-primary/10',
              dragId !== null && overIndex === i && dragId !== page.id && 'border-t-2 border-primary',
              dragId === page.id && 'opacity-40',
            )}
            onClick={(e) => {
              if (picking) {
                pickPage(page.id, e.shiftKey);
                return;
              }
              // Shift-click picks the run from the selected page to this one.
              if (library && e.shiftKey) {
                const from = selection?.kind === 'page' ? selection.id : page.id;
                setPicking(true);
                setPicked(pageRange(doc.pages, from, page.id));
                setAnchorId(page.id);
                return;
              }
              onSelect({ kind: 'page', id: page.id });
              onRevealPage(page.id);
            }}
          >
            {picking ? (
              <input
                type="checkbox"
                aria-label={`Select ${pageName}`}
                className="size-3.5 shrink-0 accent-primary"
                checked={isPicked}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  const native = e.nativeEvent;
                  pickPage(page.id, native instanceof MouseEvent && native.shiftKey);
                }}
              />
            ) : (
              <GripVertical
                aria-hidden
                className="size-3 shrink-0 cursor-grab text-muted-foreground opacity-0 group-hover:opacity-100"
              />
            )}
            <span className="w-4 text-right font-mono text-[10px] text-muted-foreground">{i + 1}</span>
            <span className="truncate font-medium">{pageName}</span>
            {page.repeat && (
              <span className="font-mono text-[9px] text-muted-foreground">×{page.repeat.over}</span>
            )}
            {picking ? null : (
              <span className="ml-auto flex gap-0.5 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100">
                {library ? (
                  <button
                    type="button"
                    aria-label="Save page to library"
                    title="Save to library"
                    className="text-muted-foreground hover:text-foreground"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSaving([page.id]);
                    }}
                  >
                    <BookmarkPlus className="size-3" />
                  </button>
                ) : null}
                <button
                  type="button"
                  aria-label="Duplicate page"
                  className="text-muted-foreground hover:text-foreground"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange(duplicatePage(doc, page.id));
                  }}
                >
                  <Copy className="size-3" />
                </button>
                <button
                  type="button"
                  aria-label="Delete page"
                  className="text-muted-foreground hover:text-destructive"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange(removePage(doc, page.id));
                    if (selected) onSelect(null);
                  }}
                >
                  <Trash2 className="size-3" />
                </button>
              </span>
            )}
          </div>
        );
      })}
      <div className="pt-2">
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() => {
            const page = { id: newPageId(), name: 'New page', blocks: [] };
            onChange(addPage(doc, page));
            onSelect({ kind: 'page', id: page.id });
          }}
        >
          <FilePlus2 /> Blank page
        </Button>
        {library ? (
          <Button variant="outline" size="sm" className="mt-1.5 w-full" onClick={() => setPickerOpen(true)}>
            <LibraryBig /> From library
          </Button>
        ) : (
          <TemplateList onAdd={(tpl) => addFromTemplate(tpl)} />
        )}
      </div>
      {library && saving !== null && savingPages.length > 0 ? (
        <SaveToLibraryDialog
          pages={savingPages}
          onSave={async (draft) => {
            const saved = await library.save(inDocumentOrder(doc.pages, saving), draft);
            stopPicking();
            return saved;
          }}
          onSuggest={() => library.suggest(inDocumentOrder(doc.pages, saving))}
          onClose={() => setSaving(null)}
        />
      ) : null}
      {library && pickerOpen ? (
        <LibraryPicker
          doc={doc}
          onAddItem={(item) => library.insert(item, insertIndexAfterSelection(doc, selection))}
          onAddTemplate={(tpl) => addFromTemplate(tpl, insertIndexAfterSelection(doc, selection))}
          onStartSaving={() => {
            setPickerOpen(false);
            setPicking(true);
          }}
          onClose={() => setPickerOpen(false)}
        />
      ) : null}
    </div>
  );
}

function InsertTab() {
  const [query, setQuery] = useState('');
  const defs = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return BLOCK_DEFS;
    return BLOCK_DEFS.filter(
      (d) => d.name.toLowerCase().includes(q) || d.description.toLowerCase().includes(q),
    );
  }, [query]);
  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          placeholder="Search blocks…"
          className="pl-7"
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      {(Object.keys(CATEGORY_LABELS) as BlockCategory[]).map((category) => {
        const inCategory = defs.filter((d) => d.category === category);
        if (inCategory.length === 0) return null;
        return (
          <div key={category} className="space-y-1">
            <h4 className="px-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              {CATEGORY_LABELS[category]}
            </h4>
            {inCategory.map((d) => (
              <PaletteCard key={d.type} type={d.type} name={d.name} description={d.description} />
            ))}
          </div>
        );
      })}
      <p className="px-1 text-[10px] leading-relaxed text-muted-foreground">
        Drag a block onto the page. Full page templates live in the Pages tab.
      </p>
    </div>
  );
}

/** The editor's left panel: pages, insert palette, outline tree, data. */
export function Panel(props: PanelProps) {
  const { doc, selection, onSelect, onChange, onRevealBlock } = props;
  const [tab, setTab] = useState<Tab>('pages');
  const [collapsed, setCollapsed] = usePersistedBoolean('proposal.panel.left.collapsed', false);

  // Collapsed: a slim rail of the tab icons — clicking one reopens to that tab.
  if (collapsed) {
    return (
      <aside className="flex w-11 shrink-0 flex-col items-center gap-1 border-r bg-card py-2">
        <button
          type="button"
          title="Expand panel"
          aria-label="Expand panel"
          className="grid size-8 place-items-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
          onClick={() => setCollapsed(false)}
        >
          <PanelLeftOpen className="size-4" />
        </button>
        <div className="my-1 h-px w-5 bg-border" />
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            title={label}
            aria-label={label}
            className="grid size-8 place-items-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
            onClick={() => {
              setTab(id);
              setCollapsed(false);
            }}
          >
            <Icon className="size-4" />
          </button>
        ))}
      </aside>
    );
  }

  return (
    <aside className="flex w-64 shrink-0 flex-col border-r bg-card">
      <div className="flex items-stretch border-b">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            title={label}
            className={cn(
              'relative flex flex-1 flex-col items-center gap-0.5 py-2 text-[10px] font-medium text-muted-foreground hover:text-foreground',
              tab === id && 'rule-active text-primary',
            )}
            onClick={() => setTab(id)}
          >
            <Icon className="size-4" />
            {label}
          </button>
        ))}
        <button
          type="button"
          title="Collapse panel"
          aria-label="Collapse panel"
          className="flex shrink-0 items-center border-l px-2 text-muted-foreground hover:bg-accent hover:text-foreground"
          onClick={() => setCollapsed(true)}
        >
          <PanelLeftClose className="size-4" />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {tab === 'pages' && <PagesTab {...props} />}
        {tab === 'insert' && <InsertTab />}
        {tab === 'outline' && (
          <div className="space-y-2">
            {doc.pages.map((page, i) => (
              <div key={page.id}>
                <p
                  className="cursor-pointer rounded px-1.5 py-1 text-[11px] font-semibold hover:bg-accent"
                  onClick={() => {
                    onSelect({ kind: 'page', id: page.id });
                    props.onRevealPage(page.id);
                  }}
                >
                  {page.name ?? `Page ${i + 1}`}
                </p>
                {page.blocks.map((node) => (
                  <OutlineNode
                    key={node.id}
                    node={node}
                    depth={1}
                    selection={selection}
                    onSelect={onSelect}
                    onReveal={onRevealBlock}
                  />
                ))}
              </div>
            ))}
          </div>
        )}
        {tab === 'data' && <DataEditor doc={doc} onChange={onChange} />}
      </div>
    </aside>
  );
}
